import { decodeAnimation, MAX_FRAMES, type DecodedAnimation } from './lib/frame-decode';
import { downloadBytes, frameFilename, frameToPng, framesToZip } from './lib/frame-export';

const PREVIEW_LIMIT = 100;

export function mountSplitApp(root: HTMLElement): () => void {
  let animation: DecodedAnimation | null = null;
  let sourceName = '';
  let selectedIndex = 0;
  let busy = false;
  let generation = 0;

  root.innerHTML = `
    <section class="card">
      <h2>选择动画</h2>
      <div class="dropzone" id="dz-split">
        <input type="file" id="file-split" accept=".apng,.png,.gif,image/png,image/gif" />
        <span class="hint">点击或拖放 APNG / PNG / GIF</span>
        <span class="fname" id="name-split"></span>
      </div>
      <p class="hint-inline">仅在浏览器本地解码。最多处理 ${MAX_FRAMES} 帧，预览最多显示前 ${PREVIEW_LIMIT} 帧。</p>
    </section>
    <section class="card" id="split-result" hidden>
      <div class="split-heading">
        <h2 id="split-summary">帧预览</h2>
        <div class="actions">
          <button type="button" class="ghost" id="btn-frame-dl" disabled>导出选中帧</button>
          <button type="button" class="primary" id="btn-zip" disabled>全部帧 ZIP</button>
        </div>
      </div>
      <p class="status info" id="split-status" role="status" aria-live="polite"></p>
      <div class="frame-grid" id="frame-grid"></div>
    </section>
  `;

  const input = root.querySelector('#file-split') as HTMLInputElement;
  const zone = root.querySelector('#dz-split') as HTMLElement;
  const name = root.querySelector('#name-split') as HTMLElement;
  const result = root.querySelector('#split-result') as HTMLElement;
  const summary = root.querySelector('#split-summary') as HTMLElement;
  const status = root.querySelector('#split-status') as HTMLElement;
  const grid = root.querySelector('#frame-grid') as HTMLElement;
  const frameDownload = root.querySelector('#btn-frame-dl') as HTMLButtonElement;
  const zipDownload = root.querySelector('#btn-zip') as HTMLButtonElement;

  function setStatus(text: string, kind: 'info' | 'ok' | 'error' = 'info'): void {
    status.textContent = text;
    status.className = `status ${kind}`;
  }

  function exportFrame(index: number): void {
    const frame = animation?.frames[index];
    if (!frame) return;
    downloadBytes(frameToPng(frame), frameFilename(sourceName, frame.index), 'image/png');
    setStatus(`已导出第 ${frame.index + 1} 帧`, 'ok');
  }

  function renderFrames(): void {
    grid.replaceChildren();
    if (!animation) return;
    for (const frame of animation.frames.slice(0, PREVIEW_LIMIT)) {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = `frame-item${frame.index === selectedIndex ? ' selected' : ''}`;
      item.title = `选择第 ${frame.index + 1} 帧`;
      const canvas = document.createElement('canvas');
      canvas.width = frame.width;
      canvas.height = frame.height;
      canvas.className = 'frame-canvas';
      canvas.getContext('2d')?.putImageData(
        new ImageData(new Uint8ClampedArray(frame.data), frame.width, frame.height),
        0,
        0,
      );
      const label = document.createElement('span');
      label.textContent = `#${String(frame.index + 1).padStart(4, '0')} · ${frame.delay} ms`;
      const exportButton = document.createElement('span');
      exportButton.className = 'frame-export';
      exportButton.textContent = '↓';
      exportButton.title = '导出此帧 PNG';
      exportButton.addEventListener('click', (event) => {
        event.stopPropagation();
        exportFrame(frame.index);
      });
      item.addEventListener('click', () => {
        selectedIndex = frame.index;
        grid.querySelector('.selected')?.classList.remove('selected');
        item.classList.add('selected');
      });
      item.append(canvas, label, exportButton);
      grid.appendChild(item);
    }
  }

  async function loadFile(file?: File): Promise<void> {
    if (!file) return;
    const currentGeneration = ++generation;
    animation = null;
    sourceName = file.name;
    selectedIndex = 0;
    grid.replaceChildren();
    result.hidden = false;
    name.textContent = file.name;
    frameDownload.disabled = true;
    zipDownload.disabled = true;
    busy = true;
    setStatus('正在本地解码…');
    try {
      const decoded = await decodeAnimation(file);
      if (currentGeneration !== generation) return;
      animation = decoded;
      summary.textContent = `${decoded.format} · ${decoded.width}×${decoded.height} · ${decoded.frames.length} 帧`;
      renderFrames();
      frameDownload.disabled = false;
      zipDownload.disabled = false;
      const truncated = decoded.truncated ? `文件超过 ${MAX_FRAMES} 帧，仅处理前 ${MAX_FRAMES} 帧。` : '';
      const preview = decoded.frames.length > PREVIEW_LIMIT ? `仅预览前 ${PREVIEW_LIMIT} 帧。` : '';
      setStatus(`${truncated}${preview}生成 ZIP 时会同时编码全部已处理帧，请预留足够内存。`, decoded.truncated ? 'error' : 'info');
    } catch (error) {
      if (currentGeneration !== generation) return;
      animation = null;
      setStatus(error instanceof Error ? error.message : '解码失败', 'error');
    } finally {
      if (currentGeneration === generation) busy = false;
    }
  }

  input.addEventListener('change', () => void loadFile(input.files?.[0]));
  zone.addEventListener('dragover', (event) => { event.preventDefault(); zone.classList.add('dragover'); });
  zone.addEventListener('dragleave', () => zone.classList.remove('dragover'));
  zone.addEventListener('drop', (event) => {
    event.preventDefault();
    zone.classList.remove('dragover');
    void loadFile(event.dataTransfer?.files?.[0]);
  });
  frameDownload.addEventListener('click', () => exportFrame(selectedIndex));
  zipDownload.addEventListener('click', async () => {
    if (!animation || busy) return;
    busy = true;
    zipDownload.disabled = true;
    setStatus('正在本地生成 ZIP，图片越大耗时越长…');
    try {
      const bytes = await framesToZip(sourceName, animation.frames);
      downloadBytes(bytes, `${sourceName.replace(/\.[^.]+$/, '')}_frames.zip`, 'application/zip');
      setStatus(`已导出 ${animation.frames.length} 帧 ZIP`, 'ok');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'ZIP 导出失败', 'error');
    } finally {
      busy = false;
      zipDownload.disabled = !animation;
    }
  });

  return () => {
    generation++;
    animation = null;
    grid.replaceChildren();
    input.value = '';
  };
}
